import { Tooltip, ToggleSwitch } from "flowbite-react";
import React, { useEffect, useMemo, useRef, useState } from "react";
import PortalTooltip from "./PortalTooltip";

export type SourceOption = {
  id: string;
  label: string;
  enabled?: boolean;
  description?: string | null;
};

type SourcesDropdownProps = {
  options: SourceOption[];
  value: Set<string>;
  onChange: (next: Set<string>) => void;

  ariaLabel?: string;

  className?: string;
  classNameButton?: string;
  disabled?: boolean;
};

const SourcesDropdown: React.FC<SourcesDropdownProps> = ({
  options,
  value,
  onChange,
  ariaLabel = "Sources",
  className,
  classNameButton,
  disabled = false,
}) => {
  const [open, setOpen] = useState(false);
  const btnRef = useRef<HTMLButtonElement | null>(null);
  const popRef = useRef<HTMLDivElement | null>(null);

  const selectedCount = value.size;

  // Close when clicking outside
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      const t = e.target as Node;
      if (!popRef.current && !btnRef.current) return;
      if (popRef.current?.contains(t) || btnRef.current?.contains(t)) return;
      setOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  // Keyboard: ESC to close, Enter/Space toggles on button
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("keydown", handler);
    return () => document.removeEventListener("keydown", handler);
  }, []);

  const normalized = useMemo<SourceOption[]>(
    () =>
      options.map((o) => ({
        ...o,
        enabled: value.has(o.id),
      })),
    [options, value]
  );

  const setChecked =
    (id: string) => (e: React.ChangeEvent<HTMLInputElement>) => {
      const next = new Set(value);
      if (e.target.checked) next.add(id);
      else next.delete(id);
      onChange(next);
    };

  return (
    <div className={className ?? ""}>
      <button
        ref={btnRef}
        type="button"
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label={ariaLabel}
        onClick={() => setOpen((s) => !s)}
        className={`relative inline-flex items-center justify-center h-10 w-10 rounded-lg border border-gray-300 bg-white  focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#0056C8] transition ${
          classNameButton ?? ""
        }`}
      >
        <img src="/globe.svg" className="w-5 h-5" alt="" />

        {selectedCount > 0 && (
          <span className="absolute -top-1.5 -right-1.5 min-w-4 h-4 px-1 rounded-full bg-red-500 text-white text-[10px] leading-4 text-center">
            {selectedCount}
          </span>
        )}
      </button>

      {/* Dropdown */}
      {open && (
        <div
          ref={popRef}
          role="menu"
          aria-label="Source list"
          className="absolute mt-2 z-50 w-60 rounded-md border border-[#E4E7EC] bg-white shadow-lg py-2 px-1"
        >
          {normalized.length === 0 ? (
            <div className="px-3 py-2 text-sm text-gray-500">No sources</div>
          ) : (
            <ul className="flex flex-col gap-1">
              {normalized.map((opt) => (
                <li
                  key={`enabled_sources_${opt.label}`}
                  className="flex items-center justify-between rounded-md px-3 py-2 "
                >
                  <PortalTooltip
                    content={opt.description ?? ""}
                    placement="bottom"
                    arrow={false}
                  >
                    <span
                      className={`text-sm text-gray-800 whitespace-nowrap ${
                        disabled ? "cursor-not-allowed" : "cursor-pointer"
                      }`}
                    >
                      {opt.label}
                    </span>
                  </PortalTooltip>

                  {/* Switch */}

                  <label
                    className={`inline-flex items-center ${
                      disabled ? "cursor-not-allowed" : "cursor-pointer"
                    } `}
                  >
                    <input
                      type="checkbox"
                      role="switch"
                      aria-checked={Boolean(opt.enabled)}
                      className="sr-only peer focus:ring-0 focus:outline-none"
                      checked={Boolean(opt.enabled)}
                      onChange={setChecked(opt.id)}
                      disabled={disabled}
                    />
                    <div
                      className="
                            relative w-9 h-5 rounded-full
                            bg-gray-300 peer-checked:bg-[#22c55e]
                            transition-colors duration-200 ease-out
                            after:content-[''] after:absolute after:top-[2px] after:start-[2px]
                            after:h-4 after:w-4 after:rounded-full after:bg-white after:border after:border-gray-300
                            after:transition-transform after:duration-200 after:ease-out
                            peer-checked:after:translate-x-[18px]
                        "
                    />
                  </label>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
};

export default SourcesDropdown;
